import React, { useEffect, useState } from 'react';
import {
	fitnessProfileFormErrorClassName,
	fitnessProfileModalBackdropClassName,
	fitnessProfileModalPanelClassName
} from './shared/fitnessProfileReadingStyles';

export default function FitnessProfileGoalProgressModal({
	isOpen,
	goal,
	onClose,
	onSave,
	saving,
	error
}) {
	const [percent, setPercent] = useState(0);

	useEffect(() => {
		if (isOpen && goal) {
			setPercent(Math.max(0, Math.min(100, goal.progress ?? goal.manual_progress_percent ?? 0)));
		}
	}, [isOpen, goal]);

	if (!isOpen || !goal) return null;

	function handleSubmit(event) {
		event.preventDefault();
		if (!saving) onSave(percent);
	}

	return (
		<div
			className={`${fitnessProfileModalBackdropClassName} max-w-none`}
			onClick={onClose}
			role="presentation"
		>
			<div
				className={`${fitnessProfileModalPanelClassName} max-w-md`}
				onClick={(event) => event.stopPropagation()}
				role="dialog"
				aria-modal="true"
				aria-labelledby="goal-progress-modal-title"
			>
				<div className="flex items-center justify-between gap-3 border-b border-gray-200 dark:border-dark-border pb-3">
					<div className="min-w-0 pr-2">
						<h3
							id="goal-progress-modal-title"
							className="text-lg font-semibold text-gray-900 dark:text-dark-text"
						>
							Update progress
						</h3>
						<p className="mt-0.5 truncate text-sm text-gray-600 dark:text-gray-400">{goal.title}</p>
					</div>
					<button
						type="button"
						onClick={onClose}
						className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-xl font-semibold leading-none text-gray-500 dark:text-dark-text-secondary hover:bg-gray-100 dark:hover:bg-dark-bg hover:text-gray-700 dark:hover:text-dark-text transition"
						aria-label="Cancel"
					>
						×
					</button>
				</div>

				<form onSubmit={handleSubmit}>
					<div className="mt-5 space-y-4">
						{error && (
							<p className={fitnessProfileFormErrorClassName} role="alert">
								{error}
							</p>
						)}
						<p className="text-sm text-gray-600 dark:text-gray-400">
							How far along are you toward {goal.target_metric || 'your target'}?
						</p>
						<div className="text-center">
							<span className="text-4xl font-semibold tabular-nums text-gray-900 dark:text-dark-text">
								{percent}%
							</span>
						</div>
						<input
							type="range"
							min="0"
							max="100"
							step="1"
							value={percent}
							onChange={(event) => setPercent(Number(event.target.value))}
							className="w-full accent-blue-500"
							aria-valuemin={0}
							aria-valuemax={100}
							aria-valuenow={percent}
							aria-label="Progress toward goal"
						/>
						<div className="flex justify-between text-xs text-gray-500 dark:text-gray-400">
							<span>Just started</span>
							<span>Complete</span>
						</div>
					</div>

					<div className="mt-6 flex justify-end gap-2">
						<button
							type="button"
							onClick={onClose}
							className="rounded-lg bg-gray-200 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-300 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-gray-600"
						>
							Cancel
						</button>
						<button
							type="submit"
							disabled={saving}
							className="rounded-lg bg-blue-500 px-4 py-2 text-sm font-medium text-white hover:bg-blue-600 disabled:opacity-60"
						>
							{saving ? 'Saving…' : 'Save progress'}
						</button>
					</div>
				</form>
			</div>
		</div>
	);
}
